import { useCallback, useEffect, useState } from 'react'
import { Button } from '@overdose/components'
import { IconBuildingStore } from '@tabler/icons-react'
import classNames from 'classnames'
import { CustomLocation } from 'shared-types'
import Input from '~/components/Input'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { useDebounce, usePage, useUserLocation } from '~/hooks'
import styles from './LocationSearch.module.css'
import { LocationSearchProps } from './LocationSearch.types'

const DEBOUNCE_DELAY = 500

export const LocationSearch = <
  TFormValues extends Record<string, unknown> = Record<string, unknown>
>({
  type = 'address',
  focusOnFirstSuggestion = true,
  useLocationApi = false,
  absoluteDropdown = true,
  onChange,
  onLocationSelected,
  ...inputProps
}: LocationSearchProps<TFormValues>) => {
  const [open, setOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<CustomLocation[]>([])
  const { countryCode } = usePage()
  const [keyword, setKeyword] = useState(inputProps.defaultValue || '')
  const debouncesKeyword = useDebounce(keyword, DEBOUNCE_DELAY)
  const { geolocateFromInternalService, searchLocations } = useUserLocation()
  const [focusingItemIdx, setFocusingItemIdx] = useState(
    focusOnFirstSuggestion ? 0 : -1
  )

  const popupVisible = open && suggestions.length > 0

  const fetchSuggestions = useCallback(
    async (key: string) => {
      const results = await geolocateFromInternalService(key, countryCode)
      setSuggestions(results)
      setFocusingItemIdx(focusOnFirstSuggestion ? 0 : -1)
    },
    [geolocateFromInternalService, countryCode, focusOnFirstSuggestion]
  )

  const fetchLocationSuggestions = useCallback(
    async (query: string) => {
      const results = await searchLocations(query, countryCode)
      const mappedResults = results.map((location) => {
        return {
          postcode: location.postcode,
          suburb: location.name,
          latitude: location.latitude,
          longitude: location.longitude,
          state: location.regionCode,
          id: null,
          type: location.type,
        } as CustomLocation
      })

      setSuggestions(mappedResults)
      setFocusingItemIdx(focusOnFirstSuggestion ? 0 : -1)
    },
    [countryCode, focusOnFirstSuggestion, searchLocations]
  )

  const handleChange = useCallback(
    (value: string) => {
      setKeyword(value)
      onChange?.(value)

      if (!value) {
        setSuggestions([])
      }
    },
    [setKeyword, setSuggestions, onChange]
  )

  const handleSelected = useCallback(
    (selected?: CustomLocation) => {
      if (!selected) {
        if (keyword) {
          onLocationSelected(null, keyword)
          setOpen(false)
        }

        return
      }

      if (type === 'postcode') {
        setKeyword(selected.postcode)
      } else {
        setKeyword(`${selected.suburb} ${selected.state} ${selected.postcode}`)
      }

      onLocationSelected(selected)
      setOpen(false)

      if (!focusOnFirstSuggestion) {
        setFocusingItemIdx(-1)
      }
    },
    [type, onLocationSelected, focusOnFirstSuggestion, keyword]
  )

  const handleKeydown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !focusOnFirstSuggestion && focusingItemIdx < 0) {
        handleSelected()
        return
      }

      if (!popupVisible) {
        return
      }

      switch (e.key) {
        case 'Enter':
          handleSelected(suggestions[focusingItemIdx])
          break
        case 'ArrowUp':
          if (focusingItemIdx > 0) {
            setFocusingItemIdx(focusingItemIdx - 1)
          }
          break
        case 'ArrowDown':
          if (focusingItemIdx < suggestions.length - 1) {
            setFocusingItemIdx(focusingItemIdx + 1)
          }
          break
        default:
          break
      }
    },
    [
      focusOnFirstSuggestion,
      focusingItemIdx,
      popupVisible,
      handleSelected,
      suggestions,
    ]
  )

  useEffect(() => {
    if (!debouncesKeyword) {
      return
    }

    if (useLocationApi) {
      fetchLocationSuggestions(debouncesKeyword.trim())
    } else {
      fetchSuggestions(debouncesKeyword)
    }
  }, [
    debouncesKeyword,
    fetchLocationSuggestions,
    fetchSuggestions,
    useLocationApi,
  ])

  useEffect(() => {
    setKeyword(inputProps.value || inputProps.defaultValue || '')
  }, [inputProps.value, inputProps.defaultValue])

  return (
    <div className={styles.wrapper}>
      <Input
        {...inputProps}
        theme={{ inner: styles.input }}
        autocomplete='off'
        value={keyword}
        onFocus={() => {
          setOpen(true)
        }}
        onBlur={() => {
          setTimeout(() => {
            setOpen(false)
          }, DEBOUNCE_DELAY)
        }}
        onClick={() => {
          setOpen(true)
        }}
        onChange={handleChange}
        onKeyDown={handleKeydown}
      />
      {popupVisible && (
        <div
          className={classNames(styles.dropdown, {
            [styles.dropdownAbsolute]: absoluteDropdown,
          })}>
          {suggestions.length > 0 && (
            <Typography
              tag={TypographyTag.p}
              variant={TypographyVariant.BodySmall}
              className={styles.resultsCount}>
              Showing {suggestions.length}{' '}
              {suggestions.length === 1 ? 'result' : 'results'}
            </Typography>
          )}
          <ul>
            {suggestions.map((sg, index) => {
              return (
                <li
                  key={index}
                  className={classNames({
                    [styles.focusedDropdownItem]: focusingItemIdx === index,
                  })}>
                  <Button
                    variant='ghost'
                    role='button'
                    onClick={() => {
                      setFocusingItemIdx(index)
                      handleSelected(sg)
                    }}>
                    {sg.type === 'store' && (
                      <IconBuildingStore
                        stroke={2}
                        className={styles.storeIcon}
                        aria-hidden='true'
                      />
                    )}
                    {sg.suburb} {sg.state} {sg.postcode}
                  </Button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
